import {
    Flex,
    Text,
    Title,
    Icon,
    PriorityIcon,
    Input
} from '@priority-software/priority-style-react';
import {observer} from 'mobx-react-lite';
import React, {useState, useEffect} from 'react';
import texts from '../../../texts';
import styles from '../../../styles.module.scss';
import {reportGeneratorsStore} from '../../../stores/ReportGeneratorsStore';
import ChartAxesInfo from './ChartAxesInfo';
import {priority} from '@priority-software/priority-proto-api/lib';
import {bubbleConfigInfo, chartConfigInfo, monitorTransactionValues} from '../../../consts';
import GaugeMetrics from './GaugeMetrics';
import XAxisSection from './XAxisSection';
import YAxisSection from './YAxisSection';
import EtcAxisSection from './EtcAxisSection';
import {IChartConfig} from '../../../types';
import {getIsSpecialTitle, handleAxesChange} from '../../../utils';
import {SingleSeriesChartTypesOptions} from '../../../chartConsts';
import ExplanatorySelector from '../ChartCreationParts/ExplanatorySelector';
import {useHover} from '../../../../../hooks/useHover';
import {helpPopoverRef} from '../../../../../_shared/Help/constants';
import {sendAnalyticsMonitor} from '../../../../../utils/monitoring';
import SecondaryXAxis from './AxisSelector';

const numericFilter = item =>
    !item.IsCompany &&
    (item.Type === priority.netitems.form.ColumnType.Int ||
        item.Type === priority.netitems.form.ColumnType.Real);

const AxesPart = observer(() => {
    const {chartInfo, currentReport, isBubble, isMultiSeries, onSaveChartInfo, checkAxesCompleted} =
        reportGeneratorsStore?.currentScreen;
    const [isAxesCompleted, setIsAxesCompleted] = useState<boolean>(checkAxesCompleted(chartInfo));
    const {setRef, isHovering} = useHover();

    useEffect(() => {
        setIsAxesCompleted(checkAxesCompleted(chartInfo));
    }, [chartInfo]);

    const isSpecialTitle = getIsSpecialTitle(chartInfo?.type as SingleSeriesChartTypesOptions);

    const columns = currentReport.columns.filter(column => !column.Hide);
    const numericColumns = columns.filter(numericFilter);

    const forceUpdate = useForceUpdate();

    const updateChartConfig = (key, value) => {
        if (isBubble) {
            let bubbleUpdate;
            if (Object.values(bubbleConfigInfo).includes(key)) {
                bubbleUpdate = {...chartInfo.bubbleConfig, [key]: value};
            } else {
                onSaveChartInfo(key, value);
                if (key === chartConfigInfo.xAxis) {
                    bubbleUpdate = {
                        ...chartInfo.bubbleConfig,
                        [bubbleConfigInfo.minX]: '',
                        [bubbleConfigInfo.maxX]: ''
                    };
                } else if (key === chartConfigInfo.yAxis) {
                    bubbleUpdate = {
                        ...chartInfo.bubbleConfig,
                        [bubbleConfigInfo.minY]: '',
                        [bubbleConfigInfo.maxY]: ''
                    };
                }
            }
            if (bubbleUpdate) {
                onSaveChartInfo(chartConfigInfo.bubbleConfig as keyof IChartConfig, bubbleUpdate);
            }
        } else {
            onSaveChartInfo(key, value);
        }
        handleAxesChange(key, chartInfo.type);
        currentReport.updateDirty();
        forceUpdate();
    };

    const handleCheckBoxHelp = e => {
        e.stopPropagation();
        helpPopoverRef.current.showHelpPopover({
            content: texts.explanatoryReportHelp,
            anchor: e.target
        });
        sendAnalyticsMonitor(monitorTransactionValues.AnalyticsExplainReportHelp);
    };

    return (
        <Flex
            direction="column"
            alignItems="flex-start"
            justifyContent="flex-start"
            style={{width: '100%'}}
        >
            <Title skin="h5" componentClasses={styles.titleWrapper}>
                {texts.enterChartDimensions}
            </Title>
            <Flex direction="row" justifyContent="space-between" alignItems="flex-start">
                <div className={styles.axesContainer}>
                    <XAxisSection
                        columns={isBubble ? numericColumns : columns}
                        chartInfo={chartInfo}
                        updateChartConfig={updateChartConfig}
                        xAsCategory={isSpecialTitle}
                    />
                    {isMultiSeries && (
                        <SecondaryXAxis
                            axisName={chartInfo.series?.Title || ''}
                            title={texts.series}
                            options={columns}
                            onSelect={e => updateChartConfig(chartConfigInfo.series, e)}
                            helpText={texts.chooseCompareFields}
                            placeholder={texts.msSecondaryXAxisPlaceHolder}
                        />
                    )}
                    <YAxisSection
                        numericColumns={numericColumns}
                        chartInfo={chartInfo}
                        updateChartConfig={updateChartConfig}
                        yAsMeasure={isSpecialTitle}
                    />
                    <EtcAxisSection
                        columns={columns}
                        numericColumns={numericColumns}
                        chartInfo={chartInfo}
                        updateChartConfig={updateChartConfig}
                    />
                    <div ref={setRef} className={styles.axisWrapper}>
                        <Text skin="label-1" componentClasses={styles.titleWithHelp}>
                            {texts.explanatoryReport}
                            {isHovering && (
                                <Icon
                                    onClick={handleCheckBoxHelp}
                                    skin="standart"
                                    icon={PriorityIcon.help}
                                />
                            )}
                        </Text>
                        {isAxesCompleted ? (
                            <ExplanatorySelector
                                selectedReport={chartInfo.explanatoryReport?.Title}
                                onSelect={e => {
                                    onSaveChartInfo(
                                        chartConfigInfo.explanatoryReport as keyof IChartConfig,
                                        e
                                    );
                                    currentReport.updateDirty();
                                }}
                            />
                        ) : (
                            <Input disabled size="large" value={'--'} />
                        )}
                    </div>
                </div>
                <GaugeMetrics chartInfo={chartInfo} />
                <ChartAxesInfo chartInfo={chartInfo} />
            </Flex>
        </Flex>
    );
});

const useForceUpdate = () => {
    const [, forceUpdate] = useState<any>({});
    return () => forceUpdate({});
};

export default AxesPart;
